import debounce from 'lodash.debounce';
import { useEffect, useRef } from 'react';

function findScrollPositions(
  listSize: number,
  childWidth: number,
  gap: number,
) {
  const edgeElementsWidth = childWidth - gap;
  const otherElementsWidth = childWidth + gap;

  const scrollPositions = [0, edgeElementsWidth];

  for (let i = 2; i < listSize; i++) {
    if (i === listSize - 1) {
      scrollPositions[i] = scrollPositions[i - 1] + edgeElementsWidth;
    } else {
      scrollPositions[i] = scrollPositions[i - 1] + otherElementsWidth;
    }
  }

  return scrollPositions;
}

function useOnSlideHandler(listRef, width, onSlide, gapOffset = 0) {
  const latestOnSlide = useRef(onSlide);

  useEffect(() => {
    const listElement = listRef.current;
    const lookup = findScrollPositions(
      listElement.children.length,
      listElement.children[0]?.offsetWidth ?? 0,
      gapOffset,
    );

    const debouncedScroll = debounce(
      () => {
        if (onSlide) {
          latestOnSlide.current = onSlide;
          const scrollLeft = listElement.scrollLeft;
          const childWidth = listElement.children[0].offsetWidth;

          if (scrollLeft === 0 || childWidth === 0) {
            latestOnSlide.current(0);
          } else {
            const closestPosition = lookup.findIndex(
              (position) => position >= scrollLeft,
            );
            latestOnSlide.current(closestPosition);
          }
        }
      },
      40,
      { leading: false, trailing: true },
    );

    listElement.addEventListener('scroll', debouncedScroll, { passive: true });

    return () => {
      listElement.removeEventListener('scroll', debouncedScroll);
      debouncedScroll.cancel();
    };
  }, [listRef, width, onSlide, gapOffset]);
}

export default useOnSlideHandler;
